import { FadeLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Card, Stack, Button, Container, Typography, TablePagination, IconButton } from '@mui/material';
import { ConfirmationDialog } from '../../components/general/ConfirmationDialog';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import { ListToolbar } from '../../components/tables';
import useQuery from '../../hooks/useQuery';
import { useApi } from '../../hooks/useApi';
import AgGridWrapper, { agDefaultColDef } from '../../components/tables/AgGridWrapper';

const BRAND = '#032C65';
const override = { margin: '0 auto' };

export default function BulletinIndex() {
  const [bulletins, setBulletins] = useState([]);
  const [selectedBulletin, setSelectedBulletin] = useState();
  const [pagination, setPagination] = useState({ total: 0, per_page: 10, current_page: 1, last_page: 1 });
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState('desc');
  const [sortField, setSortField] = useState('created_at');
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { doDelete } = useApi();
  const [loading, setLoading] = useState(true);

  const fetchData = useQuery();
  const { t } = useTranslation();

  const refreshTable = async () => {
    setIsLoading(true);
    await fetchData('bulletins', filter, sort, setBulletins, sortField, setPagination, page, () => {});
    setIsLoading(false);
  };

  useEffect(() => {
    const delay = setTimeout(refreshTable, 500);
    return () => clearTimeout(delay);
  }, [filter, sort, page, sortField]);

  const handleDelete = async () => {
    setLoading(true);
    doDelete(`bulletins/${selectedBulletin.hashid}`, t('delete_success'), t('delete_error'), refreshTable);
    setLoading(false);
    setShowDeleteDialog(false);
  };

  const columnDefs = [
    { field: 'title', headerName: t('title'), flex: 1, minWidth: 150, filter: 'agTextColumnFilter' },
    { field: 'description', headerName: t('body'), flex: 2, minWidth: 200, filter: 'agTextColumnFilter' },
    {
      field: 'document',
      headerName: t('document'),
      width: 100,
      sortable: false,
      filter: false,
      cellRenderer: (params) => params.value ? (
        <IconButton size="small" component="a" href={params.value} target="_blank" rel="noopener noreferrer" color="primary">
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      ) : null,
    },
    {
      headerName: '',
      width: 100,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      cellRenderer: (params) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <IconButton size="small" color="primary" component={RouterLink} to={`/dashboard/bulletin/edit/${params.data.hashid}`}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => { setSelectedBulletin(params.data); setShowDeleteDialog(true); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <Page title={t('bulletins')}>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>{t('bulletins')}</Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="/dashboard/bulletin/save"
            startIcon={<Iconify icon="eva:plus-fill" />}
            sx={{ background: BRAND, '&:hover': { background: '#054a9e' }, borderRadius: 2, px: 3 }}
          >
            {t('new_bulletin')}
          </Button>
        </Stack>
        <Card>
          <ListToolbar numSelected={0} filterName={filter} onFilterName={(e) => setFilter(e.target.value)} placeholder={t('bulletins')} />
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <FadeLoader color="blue" loading cssOverride={override} size={50} />
            </div>
          ) : (
            <AgGridWrapper rowData={bulletins} columnDefs={columnDefs} defaultColDef={agDefaultColDef} />
          )}
          <TablePagination
            component="div"
            rowsPerPageOptions={[]}
            count={pagination.total}
            rowsPerPage={pagination.per_page || 10}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
          />
          <ConfirmationDialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} onConfirm={handleDelete} message={t('delete_confirm')} />
        </Card>
      </Container>
    </Page>
  );
}
